<template>
  <div class="pie-chart">
    <div class="chart-container">
      <div class="chart">
        <e-charts
          :options="chartOption"
          :init-options="initOptions"
          ref="pie"
          style="height: 200px; width: 100%"
        />
      </div>
      <chart-data-table
        v-if="data.length"
        :data="chartOption.series[0].data"
        :colors="chartOption.color"
        @highlight="highlightPieSlice"
        @reset-highlight="resetPieHighlight"
      />
    </div>
  </div>
</template>

<script>
import useAppConfig from '@core/app-config/useAppConfig';
import { computed } from '@vue/composition-api';
import ECharts from 'vue-echarts';
import 'echarts/lib/chart/pie';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import ChartDataTable from './ChartDataTable.vue';

export default {
  name: 'PieChartComponent',
  components: {
    ECharts,
    ChartDataTable
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    isEmpty: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    data: {
      type: Array,
      default: () => []
    },
    doughnut: {
      type: Boolean,
      default: false
    },
    legendPosition: {
      type: String,
      default: 'right',
      validator: value => ['top', 'bottom', 'left', 'right'].includes(value)
    },
    colors: {
      type: Array,
      default: () => [
        '#7367F0', // Purple
        '#28C76F', // Green
        '#EA5455', // Red
        '#FF9F43', // Orange
        '#00CFE8', // Cyan
        '#82868B', // Gray
        '#3F8CFF', // Blue
        '#FFCB00', // Yellow
        '#FF5C93', // Pink
        '#9C27B0', // Deep Purple
        '#4CAF50', // Light Green
        '#F44336', // Light Red
        '#FFC107', // Amber
        '#03A9F4', // Light Blue
        '#607D8B', // Blue Gray
        '#FF5722', // Deep Orange
        '#00BCD4', // Teal
        '#E91E63', // Deep Pink
        '#8BC34A', // Lime
        '#673AB7' // Indigo
      ]
    }
  },
  data() {
    return {
      initOptions: {
        renderer: 'svg',
        width: 'auto',
        height: 'auto'
      }
    };
  },
  computed: {
    chartOption() {
      return {
        color: this.colors,
        tooltip: {
          trigger: 'item',
          formatter: params => {
            const percent = params.percent.toFixed(1);
            const value = params.value;
            return `<div style="padding: 2px 4px;">
              <div style="font-size: 10px;">${params.name}</div>
              <div style="font-size: 10px;"><strong>${value}</strong> (${percent}%)</div>
            </div>`;
          },
          backgroundColor: this.isDark ? 'rgba(40, 48, 70, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          borderColor: this.isDark ? '#3b4253' : '#eee',
          borderWidth: 1,
          textStyle: {
            color: this.isDark ? '#d0d2d6' : '#333',
            fontSize: 10
          },
          extraCssText:
            'box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1); border-radius: 4px; max-width: 200px; overflow-wrap: break-word; word-break: break-word;'
        },
        series: [
          {
            name: this.title,
            type: 'pie',
            radius: this.doughnut ? ['45%', '90%'] : '90%',
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 4,
              borderColor: this.isDark ? '#283046' : '#fff',
              borderWidth: 1
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: false,
                fontSize: 12,
                fontWeight: 'bold'
              },
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            data: [...this.data]
              .sort((a, b) => b.value - a.value)
              .map(item => ({
                name: item.name,
                value: item.value
              }))
          }
        ]
      };
    }
  },
  methods: {
    highlightPieSlice(index) {
      if (this.$refs.pie) {
        this.$refs.pie.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: index
        });
        this.$refs.pie.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: index
        });
      }
    },
    resetPieHighlight() {
      if (this.$refs.pie) {
        this.$refs.pie.dispatchAction({
          type: 'downplay',
          seriesIndex: 0
        });
        this.$refs.pie.dispatchAction({
          type: 'hideTip',
          seriesIndex: 0
        });
      }
    }
  },
  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.$refs.pie) {
            this.$refs.pie.refresh();
          }
        });
      },
      deep: true
    }
  },
  setup() {
    const { skin } = useAppConfig();

    const isDark = computed(() => skin.value === 'dark');

    return { skin, isDark };
  }
};
</script>

<style scoped>
.chart-container {
  display: grid;
  grid-template-columns: 1fr;
  height: 100%;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.chart {
  flex: 1;
}
</style>
